// The pathname the nav chrome should treat as active WHILE a transition animates. The router commits the URL at
// the START of a slide, but the highlight should follow the motion and flip at the midpoint. null when idle.

import { useSyncExternalStore } from 'react'
import { rtlog } from './debug'

let current: string | null = null
const listeners = new Set<() => void>()

export const navVisualPath = {
  set(value: string | null): void {
    if (value === current) return
    // A null or destination write mid-flight is the "active tab flashes to the target" bug: the chrome falls back to
    // the committed location for a frame. Recording who did it is what made the culprit findable.
    if (import.meta.env.DEV) {
      const running = progress
      if (running && running.progress > 0.02 && running.progress < 0.98 && value !== current) {
        const suspicious = value === null || (value === running.to && running.progress < 0.45)
        if (suspicious) {
          rtlog('VISUAL-PATH-SUSPECT', {
            value,
            at: Math.round(running.progress * 100) / 100,
            stack: new Error('trace').stack?.split('\n').slice(2, 6).join(' | '),
          })
        }
      }
    }
    current = value
    for (const l of listeners) l()
  },
  get: (): string | null => current,
  // An arrow, not a method: useSyncExternalStore takes it as a bare reference.
  subscribe: (l: () => void): (() => void) => {
    listeners.add(l)
    return () => {
      listeners.delete(l)
    }
  },
}

/** The transition-overridden active pathname, or `null` when idle (caller uses the real location). */
export function useNavVisualPath(): string | null {
  return useSyncExternalStore(
    navVisualPath.subscribe,
    () => navVisualPath.get(),
    () => null, // SSR: no transition in flight, header uses the real pathname
  )
}

/** Where a running slide currently is, between two members of a group. */
export interface TransitionProgress {
  from: string
  to: string
  /** 0 = fully on `from`, 1 = fully on `to`. Follows the finger exactly during a drag. */
  progress: number
}

// The exact spring trajectory the panes were handed to the compositor with, so the SlidingMarker can build its
// own animation from the same samples in the same task — one clock and one commit, however starved the main
// thread gets. Ended on a catch, and cleared with the slide.
export interface ProgressGlide {
  from: string
  to: string
  /** Spring progress per sample, evenly spaced across `durationMs` (see springSamples). */
  samples: number[]
  durationMs: number
}

// The CONTINUOUS position of the running slide, so chrome can be glued to the motion rather than told about it
// afterwards. Deliberately NOT a React hook: re-rendering a tab bar 60 times a second to move one underline is
// exactly the cost this keeps off the critical path — subscribers read it in an effect and write the DOM.
// ONE object mutated in place: a slide ticks this 60 times a second and the garbage showed up in the profile.
// Readers consume it synchronously inside their notification and must not hold the reference.
const live: TransitionProgress = { from: '', to: '', progress: 0 }
let progress: TransitionProgress | null = null
let glide: ProgressGlide | null = null
const progressListeners = new Set<() => void>()

// The only subscriber interpolates across the inter-tab distance, so a delta this small cannot move the marker a
// subpixel. The endpoints and a from/to change always publish, so it still lands exactly.
const PROGRESS_NOTIFY_EPSILON = 0.001
// Compared against rather than `live.progress`, so a run of sub-epsilon steps accumulates and still fires.
let lastNotified = -1

export const transitionProgress = {
  set(from: string, to: string, value: number): void {
    const groupChanged = from !== live.from || to !== live.to
    live.from = from
    live.to = to
    live.progress = value
    progress = live
    // Endpoints must land exactly (the marker's resting seat); a group change must re-seat immediately.
    const settled = value === 0 || value === 1
    if (!settled && !groupChanged && Math.abs(value - lastNotified) < PROGRESS_NOTIFY_EPSILON) return
    lastNotified = value
    for (const l of progressListeners) l()
  },
  clear(): void {
    if (progress === null) return // already idle — don't wake every subscriber for a no-op
    // Same trap as navVisualPath.set(null): clearing a visibly mid-flight slide re-seats the marker for a frame.
    if (import.meta.env.DEV && live.progress > 0.02 && live.progress < 0.98) {
      rtlog('PROGRESS-CLEAR-SUSPECT', {
        from: live.from,
        to: live.to,
        at: Math.round(live.progress * 100) / 100,
        stack: new Error('trace').stack?.split('\n').slice(2, 6).join(' | '),
      })
    }
    progress = null
    glide = null
    lastNotified = -1
    for (const l of progressListeners) l()
  },
  /** Announce a composited slide the chrome can join (see ProgressGlide). */
  startGlide(g: ProgressGlide): void {
    glide = g
    for (const l of progressListeners) l()
  },
  /** The composited slide ended early (a finger caught it): fall back to per-frame `set()` publishes. */
  endGlide(): void {
    if (glide === null) return
    glide = null
    for (const l of progressListeners) l()
  },
  getGlide: (): ProgressGlide | null => glide,
  get: (): TransitionProgress | null => progress,
  subscribe(l: () => void): () => void {
    progressListeners.add(l)
    return () => {
      progressListeners.delete(l)
    }
  },
}
